import { Box, Collapse, RadioGroup } from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useEffect } from "react";
import { DotLegalDialog, DotLegalRadioButton, DotLegalSelect, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { isNullOrWhitespace } from "../../../common/stringOperations";
import { useTranslation } from "../../../localization/useTranslation";
import { useAddLegalEntityDocumentHook } from "./AddLegalEntityDocumentDialog.hooks";
import DotLegalFileUploadWrapper from "../../../common/components/dotLegalFileUpload/DotLegalFileUploadWrapper";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";
import GroupEntitySelector from "@common/components/GroupEntitySelector";
import { usePlanContext } from "../../../auth/planProvider/PlanProvider";
import AttachDocumentToSharings from "../AttachDocumentToSharings";
import DotLegalSection from "@common/components/dotLegalSection/DotLegalSection.tsx";
import UploadProgressBar from "@common/components/uploadProgress/UploadProgressBar";

export interface IAddLegalEntityDocumentDialogProps {
    onCloseDialog: () => void;
    isGroupEntity: boolean;
}

function AddLegalEntityDocumentDialog(props: IAddLegalEntityDocumentDialogProps) {
    const { translateString } = useTranslation();
    const { permissions } = useUserContext();
    const { dataProtectionFeatures } = usePlanContext();
    const {
        document,
        setDocument,
        documentTypes,
        inProgress,
        validation,
        validateDocumentForm,
        userOptions,
        userOptionsIsLoading,
        documentationOption,
        onDocumentationTypeChange,
        documentStatus,
        onGroupEntityAccessIdsChange,
        onAllGroupEntitiesChange,
        gdpo,
        documentTypesIsLoading,
        isUploading,
        uploadPercentage,
        onFilesChanged,
    } = useAddLegalEntityDocumentHook(props);

    useEffect(() => {
        if (!permissions.canSetDocumentStatus) {
            let tempModel = { ...document! };
            tempModel.status = 1;
            setDocument(tempModel);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [permissions.canSetDocumentStatus]);

    function getLinkComponent() {
        return (
            <DotLegalTextField
                label={translateString("insertLink")}
                value={document?.link ?? ""}
                debounce={false}
                errorText={validation?.getErrorsForDisplay("link")}
                onChange={(value) => {
                    let tempModel = { ...document! };
                    tempModel.link = value;
                    setDocument(tempModel);
                }}
            ></DotLegalTextField>
        );
    }

    validation?.check(document);

    return (
        <DotLegalDialog
            header={translateString("uploadDocument")}
            size="md"
            isLoading={false}
            buttonOkText={translateString("save")}
            open
            onDialogClose={() => props.onCloseDialog()}
            onOkClick={() => validateDocumentForm()}
            inProgress={inProgress}
            disableBackdropOnCloseIfValueChange={document}
        >
            <Grid container columnSpacing={2} mb={2}>
                <Grid size={{ md: 6 }}>
                    <DotLegalTextField
                        label={translateString("name")}
                        value={document?.name!}
                        debounce={false}
                        onChange={(text) => {
                            let tempModel = { ...document! };
                            tempModel.name = text;
                            setDocument(tempModel);
                        }}
                        errorText={validation?.getErrorsForDisplay("name")}
                    />
                </Grid>

                <Grid size={{ md: 6 }}>
                    <DotLegalSelect
                        selectedItem={document?.documentTypeId ? document?.documentTypeId.toString() : undefined}
                        options={documentTypes}
                        placeholder={translateString("type")}
                        label={translateString("type")}
                        disableClearable
                        onChange={(documentType) => {
                            let tempModel = { ...document! };
                            tempModel.documentTypeId = documentType ?? "";
                            tempModel.addDocumentToSharings = [];
                            setDocument(tempModel);
                        }}
                        errorText={validation?.getErrorsForDisplay("documentTypeId")}
                        noOptionsLabel={translateString("noOptions")}
                        isLoading={documentTypesIsLoading}
                    />
                </Grid>

                <Grid size={{ md: 6 }}>
                    <DotLegalSelect
                        selectedItem={document?.status ? document?.status.toString() : undefined}
                        options={documentStatus}
                        placeholder={translateString("status")}
                        label={translateString("status")}
                        disableClearable
                        onChange={(status) => {
                            let tempModel = { ...document! };
                            tempModel.status = Number(status);
                            setDocument(tempModel);
                        }}
                        errorText={validation?.getErrorsForDisplay("status")}
                        noOptionsLabel={translateString("noOptions")}
                        disabled={!permissions.canSetDocumentStatus}
                    />
                </Grid>

                <Grid size={{ md: 6 }}>
                    <DotLegalSelect
                        selectedItem={document?.ownerId}
                        options={userOptions}
                        placeholder={translateString("owner")}
                        label={translateString("owner")}
                        onChange={(owner) => {
                            let tempModel = { ...document! };
                            tempModel.ownerId = owner;

                            setDocument(tempModel);
                        }}
                        isLoading={userOptionsIsLoading}
                        noOptionsLabel={translateString("noOptions")}
                    />
                </Grid>
            </Grid>
            {dataProtectionFeatures.dataProcessingAgreements && (
                <AttachDocumentToSharings
                    documentTypes={documentTypes ?? []}
                    document={document}
                    onChange={(documentUsageType) => {
                        let tempModel = { ...document! };
                        if (document?.addDocumentToSharings.includes(documentUsageType)) {
                            tempModel.addDocumentToSharings = tempModel.addDocumentToSharings.filter((usageType) => usageType !== documentUsageType);
                        } else {
                            tempModel.addDocumentToSharings.push(documentUsageType);
                        }
                        setDocument(tempModel);
                    }}
                />
            )}
            <DotLegalSection>
                <RadioGroup
                    row
                    value={documentationOption}
                    onChange={(e) => {
                        onDocumentationTypeChange(Number((e.target as HTMLInputElement).value));
                    }}
                >
                    <DotLegalRadioButton value={1} labelPlacement="end" label={translateString("uploadDocument")} />
                    <DotLegalRadioButton value={0} labelPlacement="end" label={translateString("insertLink")} />
                </RadioGroup>

                <>
                    <Collapse in={documentationOption === 1}>
                        {
                            <DotLegalFileUploadWrapper
                                errorText={validation?.getErrorsForDisplay("fileName")}
                                files={isNullOrWhitespace(document?.fileName!) ? [] : [document?.fileName!]}
                                onFilesChanged={onFilesChanged}
                                isLoading={false}
                                pasteEnabled={documentationOption === 1}
                            />
                        }
                        <UploadProgressBar isUploading={isUploading} uploadPercentage={uploadPercentage} />
                    </Collapse>
                    <Collapse in={documentationOption === 0}>
                        <Box sx={{ "& > *": { marginTop: 0 } }}>{getLinkComponent()}</Box>
                    </Collapse>
                </>
            </DotLegalSection>
            {!props.isGroupEntity && (
                <GroupEntitySelector
                    switchLabel={translateString("globalDocument")}
                    explanation={translateString("globalDocumentTooltip")}
                    selectedUserGroupCompanies={document?.groupEntityAccessIds?.map((id) => ({ userId: null, groupCompanyId: id }))}
                    onGroupCompaniesChange={onGroupEntityAccessIdsChange}
                    appliesToAllGroupEntities={document?.allGroupEntities ?? false}
                    onAppliesToAllGroupEntitiesChange={onAllGroupEntitiesChange}
                    toggleDisabled={!gdpo}
                    visibleToNonGdpo
                    autoSelectSingleGroupEntity
                    showError={!!validation?.getErrorsForDisplay("groupEntityAccessIds")}
                    errorText={validation?.getErrorsForDisplay("groupEntityAccessIds")}
                />
            )}
        </DotLegalDialog>
    );
}

export default AddLegalEntityDocumentDialog;
